/**
 * Cache handling mirrors `useUnapproveAssetStep`: the response already carries the rolled-back
 * progress, so it seeds the read cache directly, and the file detail refetches because taking
 * back an override on a completed chain sends the file back to review.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { undoAssetStepOverride } from './approval-actions.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { undoAssetStepOverride };

interface UseUndoAssetStepOverrideOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after undoing. */
  folderId?: string;
}

interface UndoAssetStepOverrideVariables {
  assetId: string;
  stepId: string;
}

export function useUndoAssetStepOverride(options: UseUndoAssetStepOverrideOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ assetId, stepId }: UndoAssetStepOverrideVariables) =>
      undoAssetStepOverride(projectId, assetId, stepId),
    onSuccess: (data, { assetId }) => {
      queryClient.setQueryData(ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId), data);
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_step_override_undone', { project_id: projectId });
    },
  });
}
